import { useState } from 'react';
import {
    ALL_SECTORS,
    RESULT_TYPES,
    filterCases,
    sectorsOf,
} from 'utils/results';
import styles from 'styles/modules/Results.module.scss';

const Chip = ({ label, pressed, type, onClick }) => (
    <button
        type="button"
        className={[styles.chip, type ? styles.chipType : ''].join(' ')}
        aria-pressed={pressed ? 'true' : 'false'}
        onClick={onClick}
    >
        {label}
    </button>
);

const Case = ({ item }) => (
    <li className={styles.case}>
        <div className={styles.meta}>
            <span className={styles.sector}>{item.sector}</span>
            <span>Seen on Google UK {item.dateSeen}</span>
        </div>
        <div className={styles.search}>
            <small>The search</small>&ldquo;{item.search}&rdquo;
        </div>
        <h3>{item.headline}</h3>
        <p>{item.detail}</p>
        <dl>
            <dt>Who</dt>
            <dd>{item.who}</dd>
            {item.howFast && (
                <>
                    <dt>How fast</dt>
                    <dd>{item.howFast}</dd>
                </>
            )}
        </dl>
        <div className={styles.tags}>
            {item.resultTags.map((tag) => (
                <span key={tag} className={styles.tag}>
                    {tag}
                </span>
            ))}
        </div>
        {item.links.length > 0 && (
            <div className={styles.links}>
                {item.links.map((link) => (
                    <a
                        key={link.url}
                        href={link.url}
                        target="_blank"
                        rel="noopener"
                    >
                        {link.label} &rarr;
                    </a>
                ))}
            </div>
        )}
    </li>
);

// The first render lists every case, so the server HTML carries all of them without JS.
const ResultsList = ({ cases }) => {
    const [sector, setSector] = useState(ALL_SECTORS);
    const [types, setTypes] = useState([]);
    const [query, setQuery] = useState('');

    const shown = filterCases(cases, { sector, types, query });
    const toggleType = (type) =>
        setTypes(
            RESULT_TYPES.filter((t) =>
                t === type ? !types.includes(t) : types.includes(t)
            )
        );

    return (
        <>
            <div className={styles.bar}>
                <label htmlFor="results-q" className={styles.hidden}>
                    Search the examples
                </label>
                <input
                    id="results-q"
                    className={styles.input}
                    type="search"
                    placeholder="Search by topic, search term or firm, e.g. RSU, bridging, Heron"
                    autoComplete="off"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                />
                <div
                    className={styles.chips}
                    role="group"
                    aria-label="Filter by sector"
                >
                    {[ALL_SECTORS, ...sectorsOf(cases)].map((s) => (
                        <Chip
                            key={s}
                            label={s}
                            pressed={s === sector}
                            onClick={() => setSector(s)}
                        />
                    ))}
                </div>
                <div
                    className={styles.chips}
                    role="group"
                    aria-label="Filter by result"
                >
                    {RESULT_TYPES.map((t) => (
                        <Chip
                            key={t}
                            label={t}
                            type
                            pressed={types.includes(t)}
                            onClick={() => toggleType(t)}
                        />
                    ))}
                </div>
                <div className={styles.count} aria-live="polite">
                    {`Showing ${shown.length} of ${cases.length} examples`}
                </div>
            </div>
            <ol className={styles.list}>
                {shown.length ? (
                    shown.map((item) => (
                        <Case
                            key={`${item.search}|${item.dateSeen}`}
                            item={item}
                        />
                    ))
                ) : (
                    <li className={styles.empty}>
                        No examples match. Try another sector or search term.
                    </li>
                )}
            </ol>
        </>
    );
};

export default ResultsList;
